/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { pgTable, text, serial, integer, timestamp, decimal, boolean, jsonb } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Mastery Graphs - containers for a learner's mastery state
export const masteryGraphs = pgTable("mastery_graphs", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  sourceGraphType: text("source_graph_type").notNull(), // 'skill_graph' or 'topic_graph'
  sourceGraphId: integer("source_graph_id").notNull(),
  title: text("title").notNull(),
  description: text("description"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  isActive: boolean("is_active").default(true),
});

// Mastery Nodes - individual mastery values for specific skills/topics
export const masteryNodes = pgTable("mastery_nodes", {
  id: serial("id").primaryKey(),
  masteryGraphId: integer("mastery_graph_id").notNull(),
  nodeType: text("node_type").notNull(), // 'skill' or 'topic'
  nodeId: integer("node_id").notNull(), // references skills.id or topics.id
  masteryValue: decimal("mastery_value", { precision: 5, scale: 4 }).notNull().default("0.0000"), // 0.0 to 1.0
  confidence: decimal("confidence", { precision: 5, scale: 4 }).notNull().default("0.0000"), // 0.0 to 1.0
  lastUpdated: timestamp("last_updated").defaultNow(),
  evidenceCount: integer("evidence_count").notNull().default(0),
});

// Learning Events - evidence events (assessment scores, activities)
export const learningEvents = pgTable("learning_events", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  eventType: text("event_type").notNull(), // 'assessment_score', 'activity_completion', 'manual_entry'
  relatedNodeType: text("related_node_type").notNull(), // 'skill' or 'topic'
  relatedNodeId: integer("related_node_id").notNull(),
  score: decimal("score", { precision: 5, scale: 4 }).notNull(), // normalized 0.0 to 1.0
  maxScore: decimal("max_score", { precision: 10, scale: 4 }),
  rawScore: decimal("raw_score", { precision: 10, scale: 4 }),
  assessmentItemId: integer("assessment_item_id"), // optional reference to assessment_items
  activityId: integer("activity_id"), // optional reference to learning activities
  metadata: jsonb("metadata"), // additional context
  createdAt: timestamp("created_at").defaultNow(),
});

// Mastery Evidence - links mastery nodes to supporting evidence
export const masteryEvidence = pgTable("mastery_evidence", {
  id: serial("id").primaryKey(),
  masteryNodeId: integer("mastery_node_id").notNull(),
  learningEventId: integer("learning_event_id").notNull(),
  weight: decimal("weight", { precision: 5, scale: 4 }).notNull().default("1.0000"), // how much this evidence contributes
  createdAt: timestamp("created_at").defaultNow(),
});

// Validation schemas for enum values
export const sourceGraphTypeSchema = z.enum(['skill_graph', 'topic_graph']);
export const nodeTypeSchema = z.enum(['skill', 'topic']);
export const eventTypeSchema = z.enum(['assessment_score', 'activity_completion', 'manual_entry']);

// Insert schemas with validation
export const insertMasteryGraphSchema = createInsertSchema(masteryGraphs, {
  sourceGraphType: sourceGraphTypeSchema,
}).omit({ 
  id: true, 
  createdAt: true, 
  updatedAt: true 
});

export const insertMasteryNodeSchema = createInsertSchema(masteryNodes, {
  nodeType: nodeTypeSchema,
}).omit({ 
  id: true, 
  lastUpdated: true 
});

export const insertLearningEventSchema = createInsertSchema(learningEvents, {
  eventType: eventTypeSchema,
  relatedNodeType: nodeTypeSchema,
  score: z.union([z.number(), z.string()]).transform(val => typeof val === 'string' ? parseFloat(val) : val).refine(val => val >= 0 && val <= 1, "Score must be between 0 and 1"),
  maxScore: z.union([z.number(), z.string()]).transform(val => typeof val === 'string' ? parseFloat(val) : val).refine(val => val > 0, "Max score must be positive").optional(),
  rawScore: z.union([z.number(), z.string()]).transform(val => typeof val === 'string' ? parseFloat(val) : val).optional(),
}).omit({ 
  id: true, 
  createdAt: true 
});

export const insertMasteryEvidenceSchema = createInsertSchema(masteryEvidence, {
  weight: z.number().min(0).max(1).optional(),
}).omit({ 
  id: true, 
  createdAt: true 
});

// TypeScript types with proper number types for decimal fields
export type MasteryGraph = typeof masteryGraphs.$inferSelect;
export type InsertMasteryGraph = z.infer<typeof insertMasteryGraphSchema>;

export type MasteryNode = Omit<typeof masteryNodes.$inferSelect, 'masteryValue' | 'confidence'> & {
  masteryValue: number;
  confidence: number;
};
export type InsertMasteryNode = z.infer<typeof insertMasteryNodeSchema>;

export type LearningEvent = Omit<typeof learningEvents.$inferSelect, 'score' | 'maxScore' | 'rawScore'> & {
  score: number;
  maxScore: number | null;
  rawScore: number | null;
};
export type InsertLearningEvent = z.infer<typeof insertLearningEventSchema>;

export type MasteryEvidence = Omit<typeof masteryEvidence.$inferSelect, 'weight'> & {
  weight: number;
};
export type InsertMasteryEvidence = z.infer<typeof insertMasteryEvidenceSchema>;

// Enum types for validation and usage
export type SourceGraphType = z.infer<typeof sourceGraphTypeSchema>;
export type NodeType = z.infer<typeof nodeTypeSchema>;
export type EventType = z.infer<typeof eventTypeSchema>;

// Extended types for API responses
export type MasteryGraphWithNodes = MasteryGraph & {
  nodes: MasteryNode[];
};

export type MasteryNodeWithEvidence = MasteryNode & {
  evidence: MasteryEvidence[];
  skill?: any; // Will be properly typed when we add the join
  topic?: any; // Will be properly typed when we add the join
};

export type LearningEventFilters = {
  eventType?: EventType;
  nodeType?: NodeType;
  nodeId?: number;
  startDate?: Date;
  endDate?: Date;
  limit?: number;
  offset?: number;
}; 